import React from 'react';
import {
  Button,
  EmptyState,
  EmptyStateBody,
  EmptyStateHeader,
  EmptyStateFooter,
  EmptyStateActions,
  EmptyStateIcon,
} from '@patternfly/react-core';
import InfoIcon from '@patternfly/react-icons/dist/esm/icons/info-circle-icon';
import ErrorIcon from '@patternfly/react-icons/dist/esm/icons/error-circle-o-icon';
import { useTranslation } from 'react-i18next';

import ExploreSubscriptions from 'components/ExploreSubscriptions';

type Props = {
  onRetry: () => void;
  initialErrorCount?: number;
  onSupportClick: () => void;
  showExploreSubscriptions: boolean;
  /** Custom title when not showing explore subscriptions (e.g. "Could not load the session") */
  title?: string;
  /** Custom body text; when not set, uses default error + retry message */
  body?: React.ReactNode;
  /** When true, always show Contact support button; otherwise only when initialErrorCount > 1 */
  alwaysShowSupport?: boolean;
};

const ErrorState = ({
  onRetry,
  onSupportClick,
  initialErrorCount,
  showExploreSubscriptions = false,
  title,
  body,
  alwaysShowSupport = false,
}: Props) => {
  const { t } = useTranslation();

  const hasTooManyRetries = initialErrorCount && initialErrorCount > 1;
  const showSupport = alwaysShowSupport || hasTooManyRetries;

  if (showExploreSubscriptions) {
    return (
      <div className="container-grid-responsive">
        <EmptyState>
          <EmptyStateHeader
            titleText={t('This course is not available in your subscription')}
            headingLevel="h4"
            icon={<EmptyStateIcon icon={InfoIcon} />}
          />
          <EmptyStateBody>
            <ExploreSubscriptions />
          </EmptyStateBody>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="container-grid-responsive">
      <EmptyState>
        <EmptyStateHeader
          titleText={title ?? t('Could not load the course')}
          headingLevel="h4"
          icon={
            <EmptyStateIcon
              icon={ErrorIcon}
              color="var(--pf-v5-global--danger-color--100)"
            />
          }
        />
        <EmptyStateBody>
          {body ??
            (hasTooManyRetries
              ? t(
                  'Our team received the error report. Meanwhile you can try again by reloading the page or contact support.',
                )
              : t(
                  'Our team received the error report. Meanwhile you can try again by reloading the page.',
                ))}
        </EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button
              variant="link"
              onClick={() => {
                onRetry();
              }}
            >
              {t('Retry')}
            </Button>
            {showSupport && (
              <Button variant="link" onClick={onSupportClick}>
                {t('Contact support')}
              </Button>
            )}
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    </div>
  );
};

export default ErrorState;
